import type { AxiosInstance } from "axios";

export type CustomerTheme = {
  themePrimaryColor?: string | null;
  themePrimaryHoverColor?: string | null;
  themeBackgroundColor?: string | null;
  themeButtonColor?: string | null;
  themeButtonHoverColor?: string | null;
  themeButtonTextColor?: string | null;
  themeButtonHoverTextColor?: string | null;
  themeButtonOutlineColor?: string | null;
  themeButtonShape?: string | null;
  themeInputShape?: string | null;
  themeCardShape?: string | null;
  themeIconColor?: string | null;
  themeNavActiveBackgroundColor?: string | null;
  themeLogo?: string | null;
  // Header theming properties
  themeHeaderStyle?: string | null; // 'default' | 'stedin' - controls header layout
  themeHeaderBackgroundColor?: string | null; // Header background color
  themeHeaderTextColor?: string | null; // Header text/link color
  themeHeaderTextHoverColor?: string | null; // Header text hover color
  themeHeaderNavActiveBackgroundColor?: string | null; // Active nav item background
  themeHeaderNavActiveTextColor?: string | null; // Active nav item text color
  themeHeaderNavHoverBackgroundColor?: string | null; // Nav item hover background
  themeHeaderNavStyle?: string | null; // 'pill' | 'fullheight' - navigation button style
  themeHeaderTopBarBackgroundColor?: string | null; // Top bar background (for stedin style)
  themeHeaderTopBarTextColor?: string | null; // Top bar text color
  themeHeaderLogoPosition?: string | null; // 'left' | 'center' - logo positioning
};

const defaultTheme: Required<CustomerTheme> = {
  themePrimaryColor: "#FF6828",
  themePrimaryHoverColor: "#F54302",
  themeBackgroundColor: "#F5F7FA",
  themeButtonColor: "#FF6828",
  themeButtonHoverColor: "#F54302",
  themeButtonTextColor: "#FFFFFF",
  themeButtonHoverTextColor: "#FFFFFF",
  themeButtonOutlineColor: "#FF6828",
  themeButtonShape: "rounded",
  themeInputShape: "rounded",
  themeCardShape: "soft",
  themeIconColor: "#FF6828",
  themeNavActiveBackgroundColor: "#E6EAF0",
  themeLogo: "/icons/logo-small.svg",
  // Header theming defaults
  themeHeaderStyle: "default",
  themeHeaderBackgroundColor: "#FFFFFF",
  themeHeaderTextColor: "#FF6828",
  themeHeaderTextHoverColor: "#F54302",
  themeHeaderNavActiveBackgroundColor: "#E6EAF0",
  themeHeaderNavActiveTextColor: "#FF6828",
  themeHeaderNavHoverBackgroundColor: "#F5F7FA",
  themeHeaderNavStyle: "pill",
  themeHeaderTopBarBackgroundColor: "#F5F7FA",
  themeHeaderTopBarTextColor: "#2D3142",
  themeHeaderLogoPosition: "left",
};

const radiusByShape: Record<string, string> = {
  square: "0px",
  soft: "16px",
  rounded: "9999px",
};

function hexToRgb(hex: string): string | null {
  const normalized = hex.trim().replace("#", "");
  if (!/^[0-9a-fA-F]{6}$/.test(normalized)) {
    return null;
  }

  const value = Number.parseInt(normalized, 16);
  return `${(value >> 16) & 255} ${(value >> 8) & 255} ${value & 255}`;
}

function setRgbVariable(name: string, color?: string | null, fallback?: string | null) {
  const rgb = hexToRgb(color ?? "") ?? hexToRgb(fallback ?? "");
  if (rgb) {
    document.documentElement.style.setProperty(name, rgb);
  } else {
    document.documentElement.style.removeProperty(name);
  }
}

export function applyCustomerTheme(theme?: CustomerTheme | null) {
  if (!import.meta.client) {
    return;
  }

  setRgbVariable("--theme-primary-rgb", theme?.themePrimaryColor, defaultTheme.themePrimaryColor);
  setRgbVariable("--theme-primary-hover-rgb", theme?.themePrimaryHoverColor, defaultTheme.themePrimaryHoverColor);
  setRgbVariable("--theme-background-rgb", theme?.themeBackgroundColor, defaultTheme.themeBackgroundColor);
  setRgbVariable("--theme-button-rgb", theme?.themeButtonColor, theme?.themePrimaryColor || defaultTheme.themeButtonColor);
  setRgbVariable("--theme-button-hover-rgb", theme?.themeButtonHoverColor, theme?.themePrimaryHoverColor || defaultTheme.themeButtonHoverColor);
  setRgbVariable("--theme-button-text-rgb", theme?.themeButtonTextColor, defaultTheme.themeButtonTextColor);
  setRgbVariable("--theme-button-hover-text-rgb", theme?.themeButtonHoverTextColor, theme?.themeButtonTextColor || defaultTheme.themeButtonHoverTextColor);
  setRgbVariable("--theme-button-outline-rgb", theme?.themeButtonOutlineColor, theme?.themeButtonColor || theme?.themePrimaryColor || defaultTheme.themeButtonOutlineColor);
  setRgbVariable("--theme-icon-rgb", theme?.themeIconColor, theme?.themePrimaryColor || defaultTheme.themeIconColor);
  setRgbVariable("--theme-nav-active-background-rgb", theme?.themeNavActiveBackgroundColor, defaultTheme.themeNavActiveBackgroundColor);
  const buttonShape: string = theme?.themeButtonShape || "rounded";
  const inputShape: string = theme?.themeInputShape || "rounded";
  const cardShape: string = theme?.themeCardShape || "soft";
  document.documentElement.style.setProperty(
    "--theme-button-radius",
    radiusByShape[buttonShape] || radiusByShape.rounded,
  );
  document.documentElement.style.setProperty(
    "--theme-input-radius",
    radiusByShape[inputShape] || radiusByShape.rounded,
  );
  document.documentElement.style.setProperty(
    "--theme-card-radius",
    radiusByShape[cardShape] || radiusByShape.soft,
  );
  
  // Apply logo
  const logo = theme?.themeLogo || defaultTheme.themeLogo;
  document.documentElement.style.setProperty(
    "--theme-logo",
    `url('${logo}')`,
  );

  // Apply header theming
  const headerStyle = theme?.themeHeaderStyle || defaultTheme.themeHeaderStyle;
  document.documentElement.style.setProperty("--theme-header-style", headerStyle);
  
  setRgbVariable("--theme-header-background-rgb", theme?.themeHeaderBackgroundColor, defaultTheme.themeHeaderBackgroundColor);
  setRgbVariable("--theme-header-text-rgb", theme?.themeHeaderTextColor, theme?.themePrimaryColor || defaultTheme.themeHeaderTextColor);
  setRgbVariable("--theme-header-text-hover-rgb", theme?.themeHeaderTextHoverColor, theme?.themePrimaryHoverColor || defaultTheme.themeHeaderTextHoverColor);
  setRgbVariable("--theme-header-nav-active-background-rgb", theme?.themeHeaderNavActiveBackgroundColor, defaultTheme.themeHeaderNavActiveBackgroundColor);
  setRgbVariable("--theme-header-nav-active-text-rgb", theme?.themeHeaderNavActiveTextColor, theme?.themeHeaderTextColor || theme?.themePrimaryColor || defaultTheme.themeHeaderNavActiveTextColor);
  setRgbVariable("--theme-header-nav-hover-background-rgb", theme?.themeHeaderNavHoverBackgroundColor, defaultTheme.themeHeaderNavHoverBackgroundColor);
  setRgbVariable("--theme-header-topbar-background-rgb", theme?.themeHeaderTopBarBackgroundColor, defaultTheme.themeHeaderTopBarBackgroundColor);
  setRgbVariable("--theme-header-topbar-text-rgb", theme?.themeHeaderTopBarTextColor, defaultTheme.themeHeaderTopBarTextColor);
  
  const logoPosition = theme?.themeHeaderLogoPosition || defaultTheme.themeHeaderLogoPosition;
  document.documentElement.style.setProperty("--theme-header-logo-position", logoPosition);
  
  const navStyle = theme?.themeHeaderNavStyle || defaultTheme.themeHeaderNavStyle;
  document.documentElement.style.setProperty("--theme-header-nav-style", navStyle);
  
  // Dispatch event so components can react to theme changes
  window.dispatchEvent(new CustomEvent('theme-changed', { detail: theme }));
}

export function applyStoredCustomerTheme() {
  if (!import.meta.client) {
    return;
  }

  const storedTheme = sessionStorage.getItem("customerTheme");
  if (!storedTheme) {
    applyCustomerTheme(null);
    return;
  }

  try {
    applyCustomerTheme(JSON.parse(storedTheme));
  } catch {
    sessionStorage.removeItem("customerTheme");
    applyCustomerTheme(null);
  }
}

export async function loadAndApplyCustomerTheme(client: AxiosInstance, accessToken?: string) {
  const response = await client.get<CustomerTheme>(
    "/api/customer/GetCurrentTheme",
    accessToken ? { headers: { Authorization: `Bearer ${accessToken}` } } : undefined,
  );

  if (import.meta.client) {
    sessionStorage.setItem("customerTheme", JSON.stringify(response.data));
    applyCustomerTheme(response.data);
  }

  return response.data;
}

export function getStoredCustomerTheme(): CustomerTheme | null {
  if (!import.meta.client) {
    return null;
  }

  const storedTheme = sessionStorage.getItem("customerTheme");
  if (!storedTheme) {
    return null;
  }

  try {
    return JSON.parse(storedTheme) as CustomerTheme;
  } catch {
    return null;
  }
}

export function getDefaultTheme(): Required<CustomerTheme> {
  return { ...defaultTheme };
}
